// Phase 11.1.5: 登入 / 註冊 / 系統 token 三模式

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Tabs, Tab, TextField, Button, Typography,
  Alert, Stack, IconButton, InputAdornment, Divider,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginWithPassword, registerWithPassword, loginWithGoogle } from '../auth';
import TelegramChip from '../components/TelegramChip';

const TAB_LOGIN = 0;
const TAB_REGISTER = 1;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
// 2026-08-01 人机验证(Cloudflare Turnstile): 拦批量注册马甲。未配 site key = 不显示、不拦(向后兼容)。
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

// React Router v6 会把某些反斜线 / 协议相对路径解释为外部跳转。
// next 只允许本站绝对路径，避免登入成功后被 URL 参数带去第三方。
export function safeNextPath(value, origin = window.location.origin) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return '/dashboard';
  }
  try {
    const target = new URL(value, origin);
    if (target.origin !== origin) return '/dashboard';
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return '/dashboard';
  }
}

export default function Login({ onLoggedIn }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Phase 12.48: URL ?tab=register / ?next=... 支持
  const initialTab = searchParams.get('tab') === 'register' ? TAB_REGISTER : TAB_LOGIN;
  const nextUrl = searchParams.get('next');   // 注册/登入后回到这里

  const [tab, setTab] = useState(initialTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Turnstile 人机验证 — 仅注册页且配了 site key 时渲染
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || tab !== TAB_REGISTER) return;
    let cancelled = false;
    const ensureScript = () => new Promise((resolve) => {
      if (window.turnstile) return resolve();
      const existing = document.querySelector('script[data-turnstile]');
      if (existing) { existing.addEventListener('load', () => resolve()); return; }
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true; s.defer = true; s.setAttribute('data-turnstile', '1');
      s.onload = () => resolve();
      document.head.appendChild(s);
    });
    ensureScript().then(() => {
      if (cancelled || !window.turnstile || !captchaRef.current) return;
      try {
        widgetIdRef.current = window.turnstile.render(captchaRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (t) => setCaptchaToken(t),
          'error-callback': () => setCaptchaToken(''),
          'expired-callback': () => setCaptchaToken(''),
        });
      } catch (e) { /* 已渲染过忽略 */ }
    });
    return () => {
      cancelled = true;
      try { if (window.turnstile && widgetIdRef.current) window.turnstile.remove(widgetIdRef.current); } catch (e) {}
      widgetIdRef.current = null; setCaptchaToken('');
    };
  }, [tab]);

  // Phase 12.29: Login 既可被父组件 mount（AuthGate locked 模式）
  // 也可作为独立 /login 路由 — 后者没 onLoggedIn callback
  // Phase 12.48: 自跳路径 = ?next= (若有) > /dashboard
  const handleSuccess = (user) => {
    if (onLoggedIn) {
      onLoggedIn(user);
    } else {
      navigate(safeNextPath(nextUrl), { replace: true });
    }
  };

  const handleGoogleCredential = React.useCallback(async (resp) => {
    setError('');
    const r = await loginWithGoogle(resp.credential);
    if (r.ok) handleSuccess(r.user);
    else setError(r.error || 'Google 登入失败');
  }, []);

  React.useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID, callback: handleGoogleCredential,
      });
      const el = document.getElementById('gsi-btn');
      if (el) window.google.accounts.id.renderButton(el, { theme: 'outline', size: 'large', width: 300 });
    };
    document.body.appendChild(s);
    return () => { try { document.body.removeChild(s); } catch (e) {} };
  }, [handleGoogleCredential]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) {
      setError('请填写邮箱和密码');
      return;
    }
    // 注册页启用了人机验证 → 必须先过验证
    if (tab === TAB_REGISTER && TURNSTILE_SITE_KEY && !captchaToken) {
      setError('请先完成下方人机验证');
      return;
    }
    setBusy(true);
    setError(null);
    const r = tab === TAB_LOGIN
      ? await loginWithPassword(email.trim(), password)
      : await registerWithPassword(email.trim(), password, captchaToken);
    setBusy(false);
    if (!r.ok) {
      setError(r.error || (tab === TAB_LOGIN ? '登入失败' : '注册失败'));
      return;
    }
    handleSuccess(r.user);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 420,
          bgcolor: 'background.paper',
          border: '1px solid rgba(167,139,250,0.2)',
          boxShadow: '0 0 40px rgba(167,139,250,0.08)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Logo / Brand */}
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
            <ShowChartIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700} color="text.primary">
              量化交易系统
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 3 }}>
            SaaS 模式 · 第一次使用请先注册
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setError(null); }}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab label="登入" />
            <Tab label="注册" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {error && <Alert severity="error" sx={{ fontSize: 13 }}>{error}</Alert>}

              <TextField
                fullWidth
                label="邮箱"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                size="small"
              />
              <TextField
                fullWidth
                label="密码"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={tab === TAB_LOGIN ? 'current-password' : 'new-password'}
                size="small"
                helperText={tab === TAB_REGISTER ? '至少 8 字符' : ' '}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPw((v) => !v)} edge="end">
                        {showPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* 人机验证 — 仅注册页且配了 site key 时显示 */}
              {tab === TAB_REGISTER && TURNSTILE_SITE_KEY && (
                <Box ref={captchaRef} sx={{ display: 'flex', justifyContent: 'center', minHeight: 65 }} />
              )}

              <Button
                type="submit"
                variant="contained"
                disabled={busy || !email || !password
                  || (tab === TAB_REGISTER && !!TURNSTILE_SITE_KEY && !captchaToken)}
                size="large"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {busy ? '处理中…' : (tab === TAB_LOGIN ? '登入' : '注册并登入')}
              </Button>
            </Stack>
          </form>

          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <div id="gsi-btn" />
          </Box>
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              忘记密码?请透过 Telegram 客服协助重置
            </Typography>
          </Box>

          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', mt: 3, fontSize: 11 }}>
            Quant Pro · AI 量化驾驶舱
          </Typography>

          {/* Phase 12.43: TG community link - 漏斗顶端社群感 */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <TelegramChip variant="default" />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
